import React, {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {Analytics} from '../../../../../services/analytics';
import {useMemoizedSelector} from '../../../../../state/hooks';
import {useRunsData, useRunSetsQuery} from '../../../../../state/runs/hooks';
import * as PanelActions from '../../../../../state/views/panel/actions';
import * as PanelBankSectionConfigActions from '../../../../../state/views/panelBankSectionConfig/actions';
import {useSingleRunModeContext} from '../../../../MultiRunWorkspace/SingleRunMode';
import {
  type MediaBrowserPanelConfig,
  transformOverlayQuery,
} from '../../../../PanelMediaBrowser';
import {useMediaSliderKey} from '../../../../PanelMediaBrowser/hooks/useMediaSliderKey';
import {
  getConfigWithDefaults,
  useAllRuns,
  useOverlays,
} from '../../../../PanelMediaBrowser/useMediaPanelValues';
import {usePanelsSharedRefsContext} from '../../../../Views/contexts/PanelsSharedRefsContext';
import {
  useUnsetWorkspaceMediaSettings,
  useUpdateWorkspaceMediaSettings,
  useWorkspaceMediaSettings,
  useWorkspaceMediaSliderKey,
} from '../../hooks';
import {
  selectAllMediaPanelConfigsWithRefs,
  selectCustomRunColors,
  selectSectionsWithMediaSettings,
} from '../mediaSettingsSelectors';
import {type MediaSettingsActionsContextValue, SettingsLevel} from '../types';
import {useWorkspaceMediaKeysAndCardTypesWithLookup} from '../useMediaCardTypesWithLookup';
import {MediaSettingsContextProvider} from './MediaSettingsContextProvider';
import {
  getNumberOfOverridesInMediaPanels,
  getNumberOfOverridesInMediaSections,
  unsetConfigFieldInMediaPanels,
  useRampFlagsForMediaSettings,
} from './utils';

/**
 * Provider for workspace-level media settings.
 *
 * This provider wraps MediaSettingsContextProvider and internally uses
 * useWorkspaceMediaSettingsDrawer to compute the context value.
 *
 * Usage:
 * ```tsx
 * <MediaWorkspaceSettingsContextProvider>
 *   <MediaSettingsDrawerContent ... />
 * </MediaWorkspaceSettingsContextProvider>
 * ```
 */
export function MediaWorkspaceSettingsContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const contextValue = useWorkspaceMediaSettingsDrawer();
  return (
    <MediaSettingsContextProvider contextValue={contextValue}>
      {children}
    </MediaSettingsContextProvider>
  );
}

/**
 * Hook to access workspace-level media settings actions.
 *
 * USE THIS HOOK when your component needs to update workspace-level settings
 * regardless of which drawer level (panel, section, workspace) it's rendered in.
 *
 * This is useful for settings like video playback sync (loop, autoplay) that
 * always apply at the workspace level even when displayed in panel/section drawers.
 *
 * Returns:
 * - updateConfig: Updates workspace media settings
 * - unsetConfigField: Removes a field from workspace media settings
 */
export function useWorkspaceMediaSettingsActions(): Pick<
  MediaSettingsActionsContextValue,
  'updateConfig' | 'unsetConfigField' | 'unsetConfigFields'
> {
  const updateWorkspaceMediaSettings = useUpdateWorkspaceMediaSettings();
  const unsetWorkspaceMediaSettings = useUnsetWorkspaceMediaSettings();

  const updateConfig = useCallback(
    (configUpdate: Partial<MediaBrowserPanelConfig>) => {
      updateWorkspaceMediaSettings(configUpdate);
      Analytics.track('Update Workspace Media Settings', {
        configUpdate: JSON.stringify(configUpdate),
      });
    },
    [updateWorkspaceMediaSettings]
  );

  const unsetConfigFields = useCallback(
    (fields: Array<keyof MediaBrowserPanelConfig>) => {
      unsetWorkspaceMediaSettings(fields);
      // `field` keeps the historical string shape for existing queries;
      // `fields` carries the structured list.
      Analytics.track('Unset Workspace Media Setting', {
        field: fields.join(','),
        fields,
      });
    },
    [unsetWorkspaceMediaSettings]
  );

  const unsetConfigField = useCallback(
    (field: keyof MediaBrowserPanelConfig) => unsetConfigFields([field]),
    [unsetConfigFields]
  );

  return {updateConfig, unsetConfigField, unsetConfigFields};
}

export const useWorkspaceMediaSettingsConfig = () => {
  const config = useWorkspaceMediaSettings();
  const {isSingleRun} = useSingleRunModeContext();
  const mediaSliderKey = useMediaSliderKey(useWorkspaceMediaSliderKey());

  return useMemo(
    () => getConfigWithDefaults(config ?? {}, mediaSliderKey, isSingleRun),
    [config, mediaSliderKey, isSingleRun]
  );
};

/**
 * Internal hook - do not use directly.
 * Use MediaWorkspaceSettingsContextProvider instead and use useMediaSettingsContext, useMediaSettingsActions, or useMediaSettingsState to access the context value(s).
 */
const useWorkspaceMediaSettingsDrawer = () => {
  const {
    mediaCardTypes,
    mediaKeys,
    loading: mediaCardTypesLoading,
  } = useWorkspaceMediaKeysAndCardTypesWithLookup();

  const {
    customRunColorsRef,
    runSetRefs,
    customRunNamesRef,
    panelBankConfigRef,
  } = usePanelsSharedRefsContext();

  const {updateConfig, unsetConfigField, unsetConfigFields} =
    useWorkspaceMediaSettingsActions();

  const customRunColors = useMemoizedSelector(
    selectCustomRunColors,
    customRunColorsRef
  );
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});

  const workspaceSettingsConfig = useWorkspaceMediaSettingsConfig();

  // Load class-label metadata for a sample of runs so we know which overlay
  // settings (masks, bounding boxes) to show.
  const panelQuery = useMemo(() => transformOverlayQuery(query), [query]);
  const {data, loading: runsLoading} = useRunsData(panelQuery);
  const loading = runsLoading || mediaCardTypesLoading;

  const allRuns = useAllRuns({
    data,
    query: panelQuery,
    customRunColors,
    pageQuery: query,
  });

  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });

  // Get sections with media settings populated for override counting
  const sectionsWithMediaSettings = useMemoizedSelector(
    selectSectionsWithMediaSettings,
    panelBankConfigRef
  );

  // Get all media browser panels with their refs for override counting/clearing
  const mediaPanelConfigsWithRefs = useMemoizedSelector(
    selectAllMediaPanelConfigsWithRefs,
    panelBankConfigRef
  );

  const mediaPanelRefs = useMemo(
    () => mediaPanelConfigsWithRefs.map(({ref}) => ref),
    [mediaPanelConfigsWithRefs]
  );

  const numberOfOverridesBelowInPanels = useMemo(
    () => getNumberOfOverridesInMediaPanels(mediaPanelConfigsWithRefs),
    [mediaPanelConfigsWithRefs]
  );
  const dispatch = useDispatch();
  const clearOverridesBelowInPanels = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      return dispatch(
        PanelActions.setConfigs(
          mediaPanelRefs,
          unsetConfigFieldInMediaPanels(mediaPanelConfigsWithRefs, field)
        )
      );
    },
    [mediaPanelConfigsWithRefs, dispatch, mediaPanelRefs]
  );

  const numberOfOverridesBelowInSections = useMemo(
    () =>
      getNumberOfOverridesInMediaSections(
        sectionsWithMediaSettings,
        workspaceSettingsConfig
      ),
    [sectionsWithMediaSettings, workspaceSettingsConfig]
  );

  const clearOverridesBelowInSections = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      sectionsWithMediaSettings.forEach(
        ({ref: sectionRef, sectionSettings}) => {
          if (sectionSettings.media?.[field] != null) {
            dispatch(
              PanelBankSectionConfigActions.unsetSectionMediaSetting(
                sectionRef,
                field
              )
            );
          }
        }
      );
    },
    [sectionsWithMediaSettings, dispatch]
  );

  const clearPanelOverridesBelowAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      clearOverridesBelowInPanels(field);
      Analytics.track('Clear Panel Media Setting Overrides From Workspace', {
        field,
        numberOfOverrides: numberOfOverridesBelowInPanels[field],
      });
    },
    [clearOverridesBelowInPanels, numberOfOverridesBelowInPanels]
  );

  const clearSectionOverridesBelowAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      clearOverridesBelowInSections(field);
      Analytics.track('Clear Section Media Setting Overrides From Workspace', {
        field,
        numberOfOverrides: numberOfOverridesBelowInSections[field],
      });
    },

    [clearOverridesBelowInSections, numberOfOverridesBelowInSections]
  );

  const rampFlags = useRampFlagsForMediaSettings();

  return useMemo(
    () => ({
      config: workspaceSettingsConfig,
      configWithoutDefaults: {}, // Product decision that we don't want to show the reset button for workspace-level settings.
      hasMasks,
      hasBoundingBoxes: hasBoxes,
      loading,
      rampFlags,
      mediaCardTypes,
      updateConfig,
      unsetConfigField,
      unsetConfigFields,
      allBoxes,
      // We'd have to fetch all the files for all the workspace's runs to determine the min and max values
      // for the slider for each of the masks. Since this is workspace level, let's just handle non-existing mins and maxes properly in ConfidenceSlider.tsx.
      sliderRanges: null,
      allMasks,
      numberOfOverridesBelow: {
        panel: numberOfOverridesBelowInPanels,
        section: numberOfOverridesBelowInSections,
      },
      clearOverridesBelow: {
        panel: clearPanelOverridesBelowAndLog,
        section: clearSectionOverridesBelowAndLog,
      },
      settingsLevel: SettingsLevel.WORKSPACE,
    }),
    [
      allBoxes,
      allMasks,
      clearPanelOverridesBelowAndLog,
      clearSectionOverridesBelowAndLog,
      hasBoxes,
      hasMasks,
      rampFlags,
      loading,
      mediaCardTypes,
      numberOfOverridesBelowInPanels,
      numberOfOverridesBelowInSections,
      unsetConfigField,
      unsetConfigFields,
      updateConfig,
      workspaceSettingsConfig,
    ]
  );
};
